import { onLCP, onFID, onINP, onCLS, onFCP, onTTFB, Metric } from 'web-vitals';
import { prodLogger } from '@/utils/productionLogger';
import { FEATURES } from '@/config/environment';

/**
 * Web Vitals monitoring integrated with Vercel Analytics
 *
 * Tracks Core Web Vitals:
 * - LCP (Largest Contentful Paint): measures loading performance
 * - FID (First Input Delay): measures interactivity (legacy, being replaced by INP)
 * - INP (Interaction to Next Paint): measures interactivity (new Core Web Vital)
 * - CLS (Cumulative Layout Shift): measures visual stability
 *
 * Also tracks additional metrics:
 * - FCP (First Contentful Paint): when first content appears
 * - TTFB (Time to First Byte): server response time
 */

/**
 * Send metric to PostHog via the proxy
 * Uses /ingest which is proxied to PostHog by Vercel
 */
const sendToAnalytics = (metric: Metric) => {
  // Skip if analytics disabled - don't even load PostHog library
  if (!FEATURES.ANALYTICS_ENABLED) return;

  try {
    // Import posthog dynamically to avoid initialization issues
    import('posthog-js').then(({ default: posthog }) => {
      posthog.capture('$web_vitals', {
        [`$web_vitals_${metric.name}_value`]: metric.value,
        [`$web_vitals_${metric.name}_rating`]: metric.rating,
        $web_vitals_metric_name: metric.name,
        $web_vitals_metric_delta: metric.delta,
        $web_vitals_metric_id: metric.id,
        $web_vitals_navigation_type: metric.navigationType,
      });
    }).catch(() => {
      // Silently fail - analytics should not break the app
    });
  } catch (error) {
    // Silently fail - analytics should not break the app
    console.debug('Web Vitals tracking failed:', error);
  }
};

/**
 * Console logging for development
 */
const logMetric = (metric: Metric) => {
  if (process.env.NODE_ENV === 'development') {
    prodLogger.debug(`[Web Vitals] ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
    });
  }
};

/**
 * Initialize Web Vitals tracking
 * Should be called once globally (in App.tsx or main.tsx)
 */
export const initWebVitals = () => {
  // Core Web Vitals
  onLCP((metric) => {
    logMetric(metric);
    sendToAnalytics(metric);
  });

  onFID((metric) => {
    logMetric(metric);
    sendToAnalytics(metric);
  });

  onINP((metric) => {
    logMetric(metric);
    sendToAnalytics(metric);
  });

  onCLS((metric) => {
    logMetric(metric);
    sendToAnalytics(metric);
  });

  // Additional metrics
  onFCP((metric) => {
    logMetric(metric);
    sendToAnalytics(metric);
  });

  onTTFB((metric) => {
    logMetric(metric);
    sendToAnalytics(metric);
  });
};
